<template>
  <b-modal v-model="showModal"
           @ok="saveAdditionalHours"
           :ok-disabled="isAddingAdditionalHours">
    <div slot="modal-title">
      {{ $t('message.my_reservations.book_additional_hours') }}
    </div>
    <section>
      <div class="row d-flex align-items-center">
        <div class="col-4 p-0">
          <label class="mb-0">{{ $t('message.my_reservations.additional_hours') }}:</label>
        </div>
        <div class="col-8 p-0">
          <div class="select-jumbo" v-if="parentServiceId">
            <select v-model="selectedData" @change="reCalculateCost">
              <option :value="duration" v-for="duration in durations">
                {{ duration.text }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <template v-if="subtotal">
        <hr/>
        <div class="row text-remark">
          <div class="col-6 p-0">
            {{ $t('message.my_reservations.summary_total.subtotal') }}
          </div>
          <div class="col-6 p-0 text-right">
            {{ subtotal | currency }}
          </div>
        </div>
        <div class="row text-remark mt-1">
          <div class="col-6 p-0">
            {{ $t('message.my_reservations.summary_total.service_vat') }}
          </div>
          <div class="col-6 p-0 text-right">
            {{ (subtotal * globalState.serviceVat) | currency }}
          </div>
        </div>
        <div class="row mt-1">
          <div class="col-6 p-0">
            <label>{{ $t('message.my_reservations.summary_total.total') }}</label>
          </div>
          <div class="col-6 p-0 text-right">
            <label>{{ (subtotal + subtotal * globalState.serviceVat) | currency }}</label>
          </div>
        </div>
      </template>
      <hr/>
      <label>{{ $t('message.booking.checkout_step.payment_method') }}</label>
      <!-- Payment with credit card -->
      <div class="card shadow-sm">
        <div class="card-body p-3">
          <div class="exp">
            <div class="checkbox">
              <input
                type="radio"
                id="paymentCreditDebit"
                name="paymentMethod"
                value="credit_card"
                v-model="paymentMethod"
              />
              <label for="paymentCreditDebit" class="mb-0">
                <span><!-- This span is needed to create the "checkbox" element --></span>
                {{ $t('message.booking.payment_step.pay_by_credit_debit_card') }}
              </label>
            </div>
          </div>
          <template v-if="payment">
            <div class="payment-method" v-if="paymentMethod === 'credit_card'">
              <payment-small :payment="payment" class="mt-3"/>
            </div>
            <template v-if="payment === null">
              <loader/>
            </template>
          </template>
          <template v-else>
            <div class="mt-3" v-if="paymentMethod === 'credit_card'">
              <payment-form ref="paymentForm" :paymentData="paymentData"/>
            </div>
          </template>
        </div>
      </div>

      <!-- Payment with bank transfer -->
      <div class="card shadow-sm">
        <div class="card-body p-3">
          <div class="exp">
            <div class="checkbox">
              <input
                type="radio"
                id="bankTransfer"
                ref="bankTransfer"
                name="paymentMethod"
                value="bank_transfer"
                v-model="paymentMethod"
              />
              <label for="bankTransfer" class="mb-0">
                <span><!-- This span is needed to create the "checkbox" element --></span>
                {{ $t('message.booking.payment_step.pay_by_bank_transfer') }}
              </label>
            </div>
          </div>
          <div class="payment-method" v-if="paymentMethod === 'bank_transfer'">
            <div class="row mt-3">
              <div class="col-12 p-0">
                <div>
                  <div>
                    <img :src="globalState.bankAccountData.image_url" width="160" class="mb-2">
                  </div>
                  <div>{{ $t('message.bank_transfer.account_name') }}: {{ globalState.bankAccountData | multiLangName
                    }}
                  </div>
                  <div class="d-flex align-items-center justify-content-center">
                    <div class="flex-grow-1">
                      <div>{{ $t('message.bank_transfer.account') }}: {{ globalState.bankAccountData.account_number }}
                      </div>
                    </div>
                    <div class="text-right">
                      <button class="btn btn-outline-info"
                              @click="isCopied = true"
                              v-clipboard:copy="globalState.bankAccountData.account_number">
                        {{ isCopied ? $t('message.button.copied') : $t('message.button.copy') }}
                      </button>
                    </div>
                  </div>
                </div>

                <hr/>

                <label>
                  {{ $t('message.bank_transfer.attach_transfer_picture') }}
                </label>
                <div class="mt-1">
                  <vue-dropzone ref="vueDropzone"
                                id="dropzone"
                                class="dropzone-dashed"
                                :options="dropzoneOptions"
                                @vdropzone-file-added="handleFileUpload"
                                @vdropzone-max-files-exceeded="vueDropZoneMaxFilesExceeded"/>
                  <div class="text-remark py-2">
                    {{ $t('message.bank_transfer.transfer_picture_remark01') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Payment with promptpay -->
      <div class="card shadow-sm">
        <div class="card-body p-3">
          <div class="exp" v-if="globalState.configData.promptpay.status === 1">
            <div class="checkbox">
              <input
                type="radio"
                id="promptpay"
                ref="promptpay"
                name="paymentMethod"
                value="promptpay"
                v-model="paymentMethod"
              />
              <label for="promptpay" class="mb-0">
                <span><!-- This span is needed to create the "checkbox" element --></span>
                {{ $t('message.booking.payment_step.pay_by_promptpay') }}
                <img
                  src="/static/img/thai_qr_payment.png"
                  width="90"
                  class="rounded ml-2"
                />
              </label>
            </div>
          </div>
          <div v-else>
            <div class="d-flex align-items-center justify-content-start">
              <div>
                <i class="fas fa-circle fa-lg -status-offline"></i>
              </div>
              <div class="pl-3">
                {{ $t('message.booking.payment_step.pay_by_promptpay') }}
              </div>
              <div>
                <img src="/static/img/thai_qr_payment.png" width="90" class="rounded ml-2"/>
              </div>
            </div>
            <div class="pt-3 text-small">
              {{ ($i18n.locale === 'th') ? globalState.configData.promptpay.remark_th : globalState.configData.promptpay.remark_en }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="paymentMethod === 'promptpay'">
        <qr-code-payment ref="qrCodePayment" @checkQrPayment="checkQrPayment"/>
        <hr/>
        <div class="text-remark">
          {{ $t('message.promptpay.payment_remark') }}
        </div>
      </div>
    </section>
    <template #modal-footer="{ ok, cancel }">
      <b-button variant="secondary" @click="closeModal()">
        {{ $t('message.button.cancel') }}
      </b-button>
      <b-button variant="info" @click="ok()"
                v-if="paymentMethod !== 'promptpay'">
        {{ isAddingAdditionalHours ? $t('message.button.loading') : $t('message.button.ok') }}
      </b-button>
    </template>

  </b-modal>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex'
  import i18n from '../../../helpers/i18nHelpers'
  import PaymentSmall from '../../payment/PaymentSmall.vue'
  import PaymentForm from '../../payment/PaymentForm.vue'
  import QrCodePayment from '../../qrPayment/QrCodePayment'
  import vue2Dropzone from 'vue2-dropzone'
  import axios from 'axios'

  export default {
    props: {
      parentServiceId: Number
    },
    components: {
      QrCodePayment,
      PaymentSmall,
      PaymentForm,
      vueDropzone: vue2Dropzone
    },
    data () {
      return {
        orderAdditionalHoursId: null,
        showModal: false,
        durations: null,
        selectedData: null,
        selectedWorkHour: '0.5',
        subtotal: null,
        serviceVat: null,
        totalPrice: null,
        paymentMethod: null,
        paymentData: {
          name: null,
          number: null,
          expirationDate: null,
          securityCode: null,
          savePayment: true,
          displaySavePayment: true,
          isDefault: false
        },
        fileUpload: null,
        isAddingAdditionalHours: false,
        omiseTokens: {
          secureToken: null,
          token: null
        },
        qrPaymentData: null,
        checkingQrPayment: null,
        qrResponse: null,
        dropzoneOptions: {
          acceptedFiles: 'image/*',
          url: 'https://beneat.co',
          thumbnailWidth: 160,
          thumbnailHeight: 160,
          maxFilesize: 5,
          dictDefaultMessage: `<i class="fal fa-cloud-upload"></i> ${this.$i18n.t('message.button.attach_slip_here')}`,
          addRemoveLinks: true,
          autoProcessQueue: false,
          maxFiles: 1
        },
        isCopied: false
      }
    },
    computed: {
      ...mapGetters({
        authState: 'auth/getState',
        state: 'orderDetail/getState',
        globalState: 'globalVal/getState',
        order: 'orderDetail/getOrderDetail',
        isUpdating: 'orderDetail/getIsUpdating',
        isUpdatingStatus: 'orderDetail/getIsUpdatingStatus',
        user: 'auth/getUser',
        payment: 'payment/getPayment',
        isSaving: 'payment/isSaving'
      }),
      ...mapState('orderDetail', ['isSavingAdditionalHours']),
      isAddingAdditionalHoursButton () {
        return this.isAddingAdditionalHours
          ? 'message.button.loading'
          : 'message.button.go_to_payment'
      }
    },
    watch: {
      totalPrice () {
        this.updateQrPaymentData()
      },
      paymentMethod () {
        this.updateQrPaymentData()
      },
      'omiseTokens.token': function (newVal, oldVal) {
        this.isAddingAdditionalHours = false
        if (this.omiseTokens.secureToken && this.omiseTokens.token) {
          const returnUri = location.href.split('?')[0] + '?tmpToken=' + this.omiseTokens.token + '&action=additional_hours_booking_payment'
          const submitData = {
            user_id: this.user.id,
            token: this.omiseTokens.secureToken,
            return_uri: returnUri,
            omise_account: 'hellobeneat'
          }
          this.checkOmiseSecure(submitData)
        }
      },
      parentServiceId () {
        if (this.parentServiceId) {
          if (this.parentServiceId === 1) {
            try {
              this.durations = [
                {
                  service_id: 1,
                  work_hour: 0.5,
                  text: '30 ' + i18n.t('message.minutes'),
                  cost: 0.5 * this.order.cost_per_hour
                },
                {
                  service_id: 1,
                  work_hour: 1.0,
                  text: '1 ' + i18n.tc('message.hour', 1),
                  cost: 1.0 * this.order.cost_per_hour
                },
                {
                  service_id: 1,
                  work_hour: 1.5,
                  text:
                    '1 ' +
                    i18n.tc('message.hour', 1) +
                    ' 30 ' +
                    i18n.t('message.minutes'),
                  cost: 1.5 * this.order.cost_per_hour
                },
                {
                  service_id: 1,
                  work_hour: 2.0,
                  text: '2 ' + i18n.tc('message.hour', 2),
                  cost: 2.0 * this.order.cost_per_hour
                },
                {
                  service_id: 1,
                  work_hour: 2.5,
                  text:
                    '2 ' +
                    i18n.tc('message.hour', 2) +
                    ' 30 ' +
                    i18n.t('message.minutes'),
                  cost: 2.5 * this.order.cost_per_hour
                },
                {
                  service_id: 1,
                  work_hour: 3.0,
                  text: '3 ' + i18n.tc('message.hour', 3),
                  cost: 3.0 * this.order.cost_per_hour
                },
                {
                  service_id: 1,
                  work_hour: 3.5,
                  text:
                    '3 ' +
                    i18n.tc('message.hour', 3) +
                    ' 30 ' +
                    i18n.t('message.minutes'),
                  cost: 3.5 * this.order.cost_per_hour
                },
                {
                  service_id: 1,
                  work_hour: 4.0,
                  text: '4 ' + i18n.tc('message.hour', 4),
                  cost: 4.0 * this.order.cost_per_hour
                },
                {
                  service_id: 1,
                  work_hour: 4.5,
                  text:
                    '4 ' +
                    i18n.tc('message.hour', 4) +
                    ' 30 ' +
                    i18n.t('message.minutes'),
                  cost: 4.5 * this.order.cost_per_hour
                },
                {
                  service_id: 1,
                  work_hour: 5.0,
                  text: '5 ' + i18n.tc('message.hour', 5),
                  cost: 5.0 * this.order.cost_per_hour
                }
              ]
            } catch (e) {
              console.log('check error', e.getMessage())
            }
          } else {
            this.durations = []
            for (let key in this.globalState.services) {
              const service = this.globalState.services[key]
              if (service.parent_id === this.parentServiceId) {
                const strText =
                  service.duration +
                  ' ' +
                  i18n.tc('message.hour', service.duration)
                const data = {
                  service_id: service.id,
                  work_hour: service.duration,
                  text: strText,
                  cost: service.cost
                }
                this.durations.push(data)
              }
            }
            this.durations = this._.orderBy(
              this.durations,
              ['work_hour'],
              ['asc']
            )
          }
          this.selectedData = this.durations[0]
        }
      },
      // Check adding payment of user
      isSaving () {
        if (!this.isSaving) {
          // this.addAdditionalHours()
        }
      },
      payment () {
        if (this.payment) {
          this.paymentMethod = 'credit_card'
        }
      }
    },
    methods: {
      ...mapActions('orderDetail', ['loadOrderDetail']),
      setOrderAdditionalHoursId (orderAdditionalHoursId) {
        this.orderAdditionalHoursId = orderAdditionalHoursId
      },
      closeModal () {
        clearInterval(this.checkingQrPayment)
        this.showModal = false
        this.paymentMethod = null
      },
      async updateQrPaymentData () {
        clearInterval(this.checkingQrPayment)
        if (this.paymentMethod === 'promptpay') {
          const url = (!this.orderAdditionalHoursId) ? 'order-additional-hours' : `order-additional-hours/${this.orderAdditionalHoursId}/update-payment`
          const submitData = {
            user_id: this.user.id,
            order_id: this.order.id,
            service_id: this.selectedData.service_id,
            work_hour: this.selectedData.work_hour,
            payment_method: this.paymentMethod
          }
          let {data} = await axios.post(url, submitData)
          if (data.error) {
            this.$swal({text: data.message, type: 'error'})
          } else {
            await this.loadOrderDetail(this.order.id)
            // insert
            const qrPaymentData = {
              order_additional_hours_id: data.order_additional_hours.id,
              amount: this.totalPrice
            }
            this.$refs.qrCodePayment.loadQrCode(qrPaymentData)
          }
        }
      },
      checkQrPayment (response) {
        this.qrResponse = response
        // Check order additional hours for replacing current transaction
        if (this.qrResponse.qr_payment && this.qrResponse.qr_payment.order_additional_hours_id) {
          this.orderAdditionalHoursId = Number(this.qrResponse.qr_payment.order_additional_hours_id)
        }
        // Clear old interval before starting a new one
        if (this.checkingQrPayment !== null) {
          clearInterval(this.checkingQrPayment)
          this.checkingQrPayment = null
        }
        this.checkingQrPayment = setInterval(() => {
          const url = '/qr-payments'
          axios
            .get(url, {
              params: {transaction_code: this.qrResponse.partnerTxnUid}
            })
            .then(async (res) => {
              const response = res.data
              if (!response.error) {
                if (response.qr_payment && response.qr_payment.status_code) {
                  if (response.qr_payment.status_code === '00') {
                    clearInterval(this.checkingQrPayment)
                    await this.loadOrderDetail(this.order.id)
                    this.closeModal()
                  } else if (response.qr_payment.status_code === '10') {
                    clearInterval(this.checkingQrPayment)
                    const alertMessage = `Error: ${response.qr_payment.error_description}`
                    this.$swal({text: alertMessage, type: 'error'})
                    // if error set qr code timeout to be expired
                    this.$refs.qrCodePayment.finishTimer()
                  }
                }
              } else {
                clearInterval(this.checkingQrPayment)
              }
            })
            .catch(error => {
              console.log('error', error)
            })
        }, 7000)
      },
      checkOmiseSecure (submitData) {
        const url = 'payments/' + this.user.id + '/check-omise-secure'
        axios
          .post(url, submitData)
          .then(res => {
            this.isAddingAdditionalHours = false
            const response = res.data
            if (!response.error) {
              this.showBookAdditionalHoursModal(false, false)
              this.$store.commit('payment/SET_TMP_OMISE_CHARGE_ID', response.omise_charge_id)
              window.location = response.authorize_uri
            } else {
              this.$swal({text: response.message, type: 'error'})
            }
          })
          .catch(error => {
            this.showBookAdditionalHoursModal(false, false)
            console.log(error)
          })
      },
      async showBookAdditionalHoursModal (status, isAdded) {
        this.showModal = status
        if (status) {
          this.fileUpload = null
          this.reCalculateCost()
          await this.$store.dispatch('payment/LOAD_PAYMENT_DETAIL', this.user.id)
          if (this.orderAdditionalHoursId && this.order.book_more_hours[0] && this.order.book_more_hours[0].id === this.orderAdditionalHoursId) {
            this.selectedData = this._.find(this.durations, {work_hour: Number(this.order.book_more_hours[0].work_hour)})
            this.reCalculateCost()
          }
        }
        // Check order additional hours is added
        if (isAdded) {
          await this.loadOrderDetail(this.order.id)
          this.$emit('onAddedOrderAdditionalHours', true)
        }
      },
      reCalculateCost () {
        this.subtotal = this.selectedData.cost
        this.serviceVat = Math.round((this.subtotal * this.globalState.serviceVat) * 100) / 100
        this.totalPrice = Number(this.subtotal) + Number(this.serviceVat)
      },
      saveAdditionalHours (evt) {
        // Prevent modal from closing
        evt.preventDefault()
        // Payment with credit card
        if (this.paymentMethod === 'credit_card') {
          if (this.payment === null) {
            if (this.$refs.paymentForm.paymentFormValidation()) {
              this.isAddingAdditionalHours = true
              // Prepare card data
              const cardNumber = this.paymentData.number.replace(/\s+/g, '')
              const arrExpiration = this.paymentData.expirationDate.split('/')
              const expirationMonth = arrExpiration[0]
              const expirationYear = 2000 + parseInt(arrExpiration[1])

              const card = {
                name: this.paymentData.name,
                number: cardNumber,
                expiration_month: expirationMonth,
                expiration_year: expirationYear,
                security_code: this.paymentData.securityCode
              }

              // Set temp additional hours booking data
              const additionalHoursBookingData = {
                user_id: this.user.id,
                order_id: this.order.id,
                order_additional_hours_id: this.orderAdditionalHoursId,
                service_id: this.selectedData.service_id,
                work_hour: this.selectedData.work_hour,
                payment_method: this.paymentMethod
              }
              this.$store.commit('orderDetail/SET_TMP_ADDITIONAL_HOURS_BOOKING_DATA', additionalHoursBookingData)

              // Perform omise process
              this.omiseTokens.secureToken = null
              this.omiseTokens.token = null
              // Get token from omise secure for otp
              window.Omise.setPublicKey(this.globalState.omisePublicKey)
              window.Omise.createToken('card', card, (statusCode, response) => {
                if (statusCode === 200 && response.object === 'token') {
                  const omiseToken = response.id
                  this.omiseTokens.secureToken = omiseToken
                  // Get token from omise non-secure for adding omise customer id
                  window.Omise.setPublicKey(this.globalState.omisePublicKey)
                  window.Omise.createToken('card', card, (statusCode, response) => {
                    if (statusCode === 200 && response.object === 'token') {
                      const omiseToken = response.id
                      this.omiseTokens.token = omiseToken
                      this.$store.commit('payment/SET_TMP_OMISE_TOKEN', omiseToken)
                    } else {
                      const alertText = 'Error: ' + response.message
                      this.$swal({text: alertText})
                      this.isAddingAdditionalHours = false
                    }
                  })
                } else {
                  const alertText = 'Error: ' + response.message
                  this.$swal({text: alertText})
                  this.isAddingAdditionalHours = false
                }
              })
            }
          } else {
            this.addAdditionalHours()
          }
        } else {
          switch (this.paymentMethod) {
            case 'bank_transfer':
              // Payment with bank transfer
              if (!this.fileUpload) {
                const alertMessage = this.$i18n.t('message.alert.no_file_upload')
                this.$swal({text: alertMessage, type: 'error'})
              } else {
                this.addAdditionalHours()
              }
              break
            case 'promptpay':
              this.addAdditionalHours()
              break
          }
        }
      },
      async addAdditionalHours () {
        this.isAddingAdditionalHours = true
        const submitData = {
          user_id: this.user.id,
          order_id: this.order.id,
          service_id: this.selectedData.service_id,
          work_hour: this.selectedData.work_hour,
          payment_method: this.paymentMethod
        }
        const url = (!this.orderAdditionalHoursId) ? 'order-additional-hours' : `order-additional-hours/${this.orderAdditionalHoursId}/update-payment`
        const {data} = await axios.post(url, submitData)
        if (!data.error) {
          this.orderAdditionalHoursId = data.order_additional_hours.id
          await this.loadOrderDetail(this.order.id)
          if (this.paymentMethod === 'credit_card' || this.paymentMethod === 'promptpay') {
            this.isAddingAdditionalHours = false
            await this.showBookAdditionalHoursModal(false, true)
            this.closeModal()
          } else if (this.paymentMethod === 'bank_transfer') {
            await this.submitFileUpload(this.orderAdditionalHoursId)
          }
        } else {
          switch (data.error_case) {
            case 'INVALID_CREDIT_CARD':
              this.$swal({text: this.$i18n.t('message.alert.invalid_credit_card'), type: 'error'})
              break
            case 'CLEANER_CHECKED_OUT':
              this.$swal({text: this.$i18n.t('message.alert.cleaner_checked_out'), type: 'error'})
              break
            case 'NO_DATA':
              this.$swal({text: this.$i18n.t('message.alert.cannot_book_additional_hours'), type: 'error'})
              break
          }
        }
        // this.$store.dispatch('orderDetail/ADD_ADDITIONAL_HOURS', submitData)
      },
      handleFileUpload (file) {
        this.fileUpload = file
      },
      async submitFileUpload (orderAdditionalHoursId) {
        let formData = new FormData()
        formData.append('upload_image', this.fileUpload)
        const url = `order-additional-hours/${orderAdditionalHoursId}/bank-transfer`
        const {data} = await axios.post(url, formData)
        if (!data.error) {
          this.isAddingAdditionalHours = false
          await this.showBookAdditionalHoursModal(false, true)
        } else {
          this.$swal({text: data.message, type: 'error'})
        }
      },
      vueDropZoneMaxFilesExceeded (file) {
        this.$refs.vueDropzone.removeAllFiles()
        this.$refs.vueDropzone.addFile(file)
      }
    },
    beforeDestroy () {
      clearInterval(this.checkingQrPayment)
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/order/components/BookAdditionalHoursModal.vue